/**
 * LiquiditaetPage V6 — Agicap-Style Liquiditätsplanung
 * 
 * V6 Verbesserungen:
 * - Chart und Tabelle sind SYNCHRONISIERT (gleiche Spaltenbreiten, ein scrollbarer Container)
 * - Datenlücken werden visuell markiert (z.B. Jan 24 – Mär 25)
 * - Datenqualitäts-Indikator pro Monat (Anzahl Transaktionen, Validierungsstatus)
 * - Intelligente Warnungen bei Anomalien
 * - Dynamische Prognose-Erkennung
 * - Banner für offene Klärungen
 */

import { useState, useMemo, useRef, useCallback } from 'react';
import {
  ArrowUpRight, ArrowDownRight, Activity, Wallet, TrendingUp, TrendingDown,
  ChevronDown, ChevronRight, Building2, Users, Download,
  Plus, Edit3, Trash2, Save, X, Flame, Timer, BarChart3, Table,
  Eye, EyeOff, RefreshCw, Info, AlertTriangle, ExternalLink,
  ShieldCheck, ShieldAlert, FileWarning, CheckCircle2
} from 'lucide-react';
import { useLocation } from 'wouter';
import {
  useHistorischeDaten,
  KATEGORIE_LABELS,
  type TabellenZeile,
} from '@/hooks/useHistorischeDaten';

// ============================================================
// HELPER
// ============================================================

function formatEuro(betrag: number, compact = false): string {
  if (compact && Math.abs(betrag) >= 1000) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(betrag);
  }
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(betrag);
}

const FIRMEN = [
  { id: 'alle', label: 'Konsolidiert' },
  { id: 'sv_buero_koop', label: 'SV Büro Koop' },
  { id: 'lcs', label: 'LCS GmbH' },
];

const ICON_MAP: Record<string, any> = {
  ArrowUpRight, ArrowDownRight, Activity, Wallet, TrendingUp, Flame, Timer
};

/** Erkennt Lücken in den Monaten (> 1 Monat Abstand) */
function detectGaps(monate: string[]): Set<number> {
  const gapIndices = new Set<number>();
  for (let i = 1; i < monate.length; i++) {
    const [y1, m1] = monate[i - 1].split('-').map(Number);
    const [y2, m2] = monate[i].split('-').map(Number);
    const diff = (y2 * 12 + m2) - (y1 * 12 + m1);
    if (diff > 1) {
      gapIndices.add(i); // Lücke VOR diesem Index
    }
  }
  return gapIndices;
}

/** Formatiert die Lücke als Text */
function formatGap(monat1: string, monat2: string, formatLabel: (m: string) => string): string {
  const [y1, m1] = monat1.split('-').map(Number);
  const [y2, m2] = monat2.split('-').map(Number);
  const diff = (y2 * 12 + m2) - (y1 * 12 + m1) - 1;
  // Berechne den ersten fehlenden Monat
  const d1 = new Date(y1, m1, 1); // Monat nach monat1
  const labels = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  const startLabel = `${labels[d1.getMonth()]} ${String(d1.getFullYear()).slice(2)}`;
  // Letzter fehlender Monat
  const d2 = new Date(y2, m2 - 2, 1);
  const endLabel = `${labels[d2.getMonth()]} ${String(d2.getFullYear()).slice(2)}`;
  return diff === 1 ? startLabel : `${startLabel} – ${endLabel}`;
}

// Spaltenbreite für synchronisierten Chart + Tabelle
const COL_WIDTH = 90; // px pro Monatsspalte
const LABEL_COL_WIDTH = 220; // px für Kategorie-Spalte
const SUMMARY_COL_WIDTH = 85; // px für Summe/Ø-Spalten
const GAP_COL_WIDTH = 40; // px für Lücken-Markierung

// ============================================================
// KPI-KARTE
// ============================================================
function KpiKarte({ label, wert, sub, icon: Icon, farbe, trend }: {
  label: string; wert: string; sub?: string; icon: any; farbe: string; trend?: 'up' | 'down' | 'neutral';
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${farbe}15` }}>
          <Icon className="w-4 h-4" style={{ color: farbe }} />
        </div>
      </div>
      <div className="text-xl font-bold text-gray-900">{wert}</div>
      {sub && (
        <div className={`text-xs mt-1 flex items-center gap-1 ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-500' : 'text-gray-500'}`}>
          {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : trend === 'down' ? <TrendingDown className="w-3 h-3" /> : null}
          {sub}
        </div>
      )}
    </div>
  );
}

// ============================================================
// INTEGRIERTER CHART (als Teil der Tabelle, gleiche Spaltenbreiten)
// ============================================================
function IntegrierterChart({ monatsDaten, gaps, formatLabel }: {
  monatsDaten: any[]; gaps: Set<number>; formatLabel: (m: string) => string;
}) {
  const maxWert = Math.max(...monatsDaten.map(m => Math.max(m.einnahmen, m.ausgaben, 1)));
  const chartHeight = 160;

  return (
    <div className="flex" style={{ height: `${chartHeight + 40}px` }}>
      {/* Leere Kategorie-Spalte (synchron mit Tabelle) */}
      <div className="flex-shrink-0 flex flex-col justify-end pb-6 pr-2" style={{ width: `${LABEL_COL_WIDTH}px` }}>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Ein</div>
          <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-red-400" /> Aus</div>
          <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-gray-300 border border-dashed border-gray-400" /> Prog.</div>
        </div>
      </div>
      {/* Chart-Balken — gleiche Breite wie Tabellenspalten */}
      <div className="flex items-end">
        {monatsDaten.map((m, idx) => (
          <div key={m.monat}>
            {/* Lücken-Markierung */}
            {gaps.has(idx) && (
              <div className="inline-flex flex-col items-center justify-end" style={{ width: `${GAP_COL_WIDTH}px`, height: `${chartHeight + 40}px` }}>
                <div className="w-px h-full border-l-2 border-dashed border-amber-300 relative">
                  <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 bg-amber-100 text-amber-600 text-[8px] font-medium px-1 py-0.5 rounded whitespace-nowrap rotate-[-90deg]">
                    Lücke
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col items-center group relative" style={{ width: `${COL_WIDTH}px` }}>
              {/* Tooltip */}
              <div className="absolute bottom-full mb-1 hidden group-hover:block z-20 bg-gray-900 text-white text-[10px] rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg pointer-events-none">
                <div className="font-semibold">{m.label} {m.istPrognose ? '(Prognose)' : ''}</div>
                <div className="text-emerald-300">Ein: {formatEuro(m.einnahmen)}</div>
                <div className="text-red-300">Aus: {formatEuro(m.ausgaben)}</div>
                <div className={m.cashflow >= 0 ? 'text-emerald-300' : 'text-red-300'}>CF: {formatEuro(m.cashflow)}</div>
              </div>
              {/* Balken */}
              <div className="w-full flex gap-0.5 items-end px-1" style={{ height: `${chartHeight}px` }}>
                <div className={`flex-1 rounded-t-sm transition-all ${m.istPrognose ? 'opacity-50 border border-dashed border-emerald-400 bg-emerald-100' : 'bg-emerald-500'}`}
                  style={{ height: `${Math.max(2, (m.einnahmen / maxWert) * 100)}%` }} />
                <div className={`flex-1 rounded-t-sm transition-all ${m.istPrognose ? 'opacity-50 border border-dashed border-red-300 bg-red-100' : 'bg-red-400'}`}
                  style={{ height: `${Math.max(2, (m.ausgaben / maxWert) * 100)}%` }} />
              </div>
              {/* Cashflow-Wert */}
              <div className="mt-1 text-center" style={{ height: '32px' }}>
                <span className={`text-[10px] font-medium block ${m.cashflow >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {formatEuro(m.cashflow, true)}
                </span>
                <span className="text-[9px] text-gray-400 block">{m.label}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Leere Summe/Ø-Spalten (synchron) */}
      <div style={{ width: `${SUMMARY_COL_WIDTH * 2}px` }} className="flex-shrink-0" />
    </div>
  );
}

// ============================================================
// AGICAP-STYLE TABELLENZEILE
// ============================================================
function ZeileRow({ zeile, monate, expandedRows, onToggle, level = 0, formatLabel, prognoseMonate, gaps }: {
  zeile: TabellenZeile; monate: string[]; expandedRows: Set<string>; onToggle: (id: string) => void;
  level?: number; formatLabel: (m: string) => string; prognoseMonate: Set<string>; gaps: Set<number>;
}) {
  const isExpanded = expandedRows.has(zeile.id);
  const hasChildren = zeile.children && zeile.children.length > 0;
  const IconComp = zeile.icon ? ICON_MAP[zeile.icon] : null;

  const isRoot = zeile.level === 0;
  const isSumme = zeile.typ === 'summe' || zeile.typ === 'saldo';
  const isKpi = zeile.typ === 'kpi';

  const rowBg = isSumme ? 'bg-gray-50 border-t-2 border-gray-200'
    : isKpi ? 'bg-blue-50/50 border-t border-blue-100'
    : isRoot ? 'bg-white border-t border-gray-100'
    : level === 1 ? 'bg-gray-50/50' : 'bg-white';

  const textWeight = isRoot || isSumme ? 'font-semibold' : level === 1 ? 'font-medium' : 'font-normal';
  const textSize = isRoot ? 'text-sm' : 'text-xs';
  const textColor = isSumme
    ? (Object.values(zeile.werte).reduce((a, b) => a + b, 0) >= 0 ? 'text-emerald-700' : 'text-red-600')
    : zeile.typ === 'einnahme' ? 'text-emerald-700'
    : zeile.typ === 'ausgabe' ? 'text-red-600'
    : isKpi ? 'text-blue-700' : 'text-gray-900';

  const histWerte = Object.entries(zeile.werte).filter(([m]) => !prognoseMonate.has(m));
  const histSumme = histWerte.reduce((s, [, v]) => s + v, 0);
  const histCount = histWerte.filter(([, v]) => v !== 0).length || 1;

  return (
    <>
      <tr className={`${rowBg} hover:bg-gray-100/50 transition-colors ${hasChildren ? 'cursor-pointer' : ''} group`}
        onClick={() => hasChildren && onToggle(zeile.id)}>
        <td className={`sticky left-0 z-10 ${rowBg} px-3 py-2 whitespace-nowrap`}
          style={{ paddingLeft: `${12 + level * 20}px`, width: `${LABEL_COL_WIDTH}px`, minWidth: `${LABEL_COL_WIDTH}px`, maxWidth: `${LABEL_COL_WIDTH}px` }}>
          <div className="flex items-center gap-2">
            {hasChildren ? (
              <button className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600">
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            ) : <span className="w-4" />}
            {IconComp && (
              <div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: `${zeile.farbe || '#6b7280'}15` }}>
                <IconComp className="w-3 h-3" style={{ color: zeile.farbe || '#6b7280' }} />
              </div>
            )}
            {zeile.farbe && !IconComp && level > 0 && (
              <div className="w-1.5 h-4 rounded-full" style={{ backgroundColor: zeile.farbe }} />
            )}
            <span className={`${textWeight} ${textSize} ${textColor} truncate`}>{zeile.label}</span>
            {hasChildren && (
              <span className="text-[10px] text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {zeile.anzahlChildren || zeile.children?.length}
              </span>
            )}
          </div>
        </td>
        {monate.map((monat, idx) => {
          const wert = zeile.werte[monat] || 0;
          const istPrognose = prognoseMonate.has(monat);
          return (
            <React.Fragment key={monat}>
              {/* Lücken-Spalte */}
              {gaps.has(idx) && (
                <td style={{ width: `${GAP_COL_WIDTH}px`, minWidth: `${GAP_COL_WIDTH}px` }}
                  className="bg-amber-50/50 border-x border-dashed border-amber-200" />
              )}
              <td style={{ width: `${COL_WIDTH}px`, minWidth: `${COL_WIDTH}px` }}
                className={`px-2 py-2 text-right whitespace-nowrap ${textSize} ${istPrognose ? 'bg-blue-50/30' : ''}`}>
                <span className={`${textWeight} ${wert === 0 ? 'text-gray-300' : textColor} ${istPrognose ? 'italic' : ''}`}>
                  {wert === 0 ? '—' : formatEuro(wert, true)}
                </span>
              </td>
            </React.Fragment>
          );
        })}
        <td className="px-3 py-2 text-right whitespace-nowrap bg-gray-50 border-l border-gray-200"
          style={{ width: `${SUMMARY_COL_WIDTH}px`, minWidth: `${SUMMARY_COL_WIDTH}px` }}>
          <span className={`${textWeight} ${textSize} ${textColor}`}>{formatEuro(histSumme, true)}</span>
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap bg-gray-50"
          style={{ width: `${SUMMARY_COL_WIDTH}px`, minWidth: `${SUMMARY_COL_WIDTH}px` }}>
          <span className={`${textWeight} ${textSize} text-gray-600`}>{formatEuro(histSumme / histCount, true)}</span>
        </td>
      </tr>
      {isExpanded && zeile.children?.map(child => (
        <ZeileRow key={child.id} zeile={child} monate={monate} expandedRows={expandedRows} onToggle={onToggle} level={level + 1} formatLabel={formatLabel} prognoseMonate={prognoseMonate} gaps={gaps} />
      ))}
    </>
  );
}

// ============================================================
// DATENQUALITÄTS-BANNER
// ============================================================
function DatenqualitaetBanner({ monatsDaten, offeneKlaerungen, prognoseMonate }: {
  monatsDaten: any[]; offeneKlaerungen: number; prognoseMonate: Set<string>;
}) {
  const histMonate = monatsDaten.filter(m => !prognoseMonate.has(m.monat));
  const monateOhneDaten = histMonate.filter(m => m.einnahmen === 0 && m.ausgaben === 0);
  const totalTx = histMonate.reduce((s, m) => s + (m.einnahmen > 0 || m.ausgaben > 0 ? 1 : 0), 0);

  // Anomalie-Erkennung: Monate mit ungewöhnlich hohen/niedrigen Werten
  const avgCF = histMonate.reduce((s, m) => s + m.cashflow, 0) / (histMonate.length || 1);
  const stdDev = Math.sqrt(histMonate.reduce((s, m) => s + Math.pow(m.cashflow - avgCF, 2), 0) / (histMonate.length || 1));
  const anomalien = histMonate.filter(m => Math.abs(m.cashflow - avgCF) > 2 * stdDev);

  const qualitaetScore = Math.round(
    ((histMonate.length - monateOhneDaten.length) / Math.max(histMonate.length, 1)) * 100
    * (1 - Math.min(offeneKlaerungen / 5000, 0.5))
  );

  const scoreColor = qualitaetScore >= 80 ? 'text-emerald-600' : qualitaetScore >= 50 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = qualitaetScore >= 80 ? 'bg-emerald-50 border-emerald-200' : qualitaetScore >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
  const ScoreIcon = qualitaetScore >= 80 ? ShieldCheck : qualitaetScore >= 50 ? ShieldAlert : FileWarning;

  return (
    <div className={`${scoreBg} border rounded-xl p-4`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${qualitaetScore >= 80 ? 'bg-emerald-100' : qualitaetScore >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
          <ScoreIcon className={`w-6 h-6 ${scoreColor}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-sm font-bold text-gray-900">Datenqualität</h3>
            <span className={`text-lg font-bold ${scoreColor}`}>{qualitaetScore}%</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
            <div className="text-xs">
              <span className="text-gray-500">Historische Monate</span>
              <div className="font-semibold text-gray-900">{histMonate.length} Monate</div>
            </div>
            <div className="text-xs">
              <span className="text-gray-500">Offene Klärungen</span>
              <div className={`font-semibold ${offeneKlaerungen > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {offeneKlaerungen.toLocaleString('de-DE')} Posten
              </div>
            </div>
            <div className="text-xs">
              <span className="text-gray-500">Monate ohne Daten</span>
              <div className={`font-semibold ${monateOhneDaten.length > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {monateOhneDaten.length}
              </div>
            </div>
            <div className="text-xs">
              <span className="text-gray-500">Anomalien erkannt</span>
              <div className={`font-semibold ${anomalien.length > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {anomalien.length} {anomalien.length > 0 ? `(${anomalien.map(a => a.label).join(', ')})` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MITARBEITER-VERWALTUNG
// ============================================================
function MitarbeiterSection({ mitarbeiter, onSave, onDelete }: {
  mitarbeiter: any[]; onSave: (ma: any) => void; onDelete: (id: string) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [editMa, setEditMa] = useState<any>(null);
  const gesamtKosten = mitarbeiter.reduce((s, m) => s + (m.gesamt_kosten || m.brutto_gehalt + m.ag_anteil_sv + (m.sonstige_kosten || 0)), 0);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-orange-500" />
          <h3 className="text-sm font-semibold text-gray-900">Mitarbeiter & Personalkosten</h3>
          <span className="text-xs bg-orange-100 text-orange-700 rounded-full px-2 py-0.5">{mitarbeiter.length}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-gray-900">{formatEuro(gesamtKosten)}/Monat</span>
          <button onClick={() => { setEditMa(null); setShowForm(true); }} className="text-xs bg-teal-600 text-white rounded-lg px-3 py-1.5 hover:bg-teal-700 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Hinzufügen
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 text-gray-500 font-medium">Name</th>
              <th className="text-left py-2 px-3 text-gray-500 font-medium">Position</th>
              <th className="text-left py-2 px-3 text-gray-500 font-medium">Firma</th>
              <th className="text-left py-2 px-3 text-gray-500 font-medium">Typ</th>
              <th className="text-right py-2 px-3 text-gray-500 font-medium">Brutto</th>
              <th className="text-right py-2 px-3 text-gray-500 font-medium">AG-SV</th>
              <th className="text-right py-2 px-3 text-gray-500 font-medium">Sonstige</th>
              <th className="text-right py-2 px-3 text-gray-500 font-medium">Gesamt</th>
              <th className="text-right py-2 px-3 text-gray-500 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {mitarbeiter.map(ma => {
              const gesamt = ma.gesamt_kosten || (ma.brutto_gehalt + ma.ag_anteil_sv + (ma.sonstige_kosten || 0));
              return (
                <tr key={ma.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2.5 px-3 font-medium text-gray-900">{ma.name}</td>
                  <td className="py-2.5 px-3 text-gray-600">{ma.position || '—'}</td>
                  <td className="py-2.5 px-3"><span className="bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 text-[10px]">{ma.firma === 'sv_buero_koop' ? 'SV Büro' : ma.firma === 'lcs' ? 'LCS' : ma.firma}</span></td>
                  <td className="py-2.5 px-3"><span className={`rounded px-1.5 py-0.5 text-[10px] ${ma.typ === 'vollzeit' ? 'bg-emerald-100 text-emerald-700' : ma.typ === 'teilzeit' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>{ma.typ}</span></td>
                  <td className="py-2.5 px-3 text-right font-medium">{formatEuro(ma.brutto_gehalt)}</td>
                  <td className="py-2.5 px-3 text-right text-gray-600">{formatEuro(ma.ag_anteil_sv)}</td>
                  <td className="py-2.5 px-3 text-right text-gray-600">{formatEuro(ma.sonstige_kosten || 0)}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-red-600">{formatEuro(gesamt)}</td>
                  <td className="py-2.5 px-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditMa(ma); setShowForm(true); }} className="p-1 hover:bg-gray-200 rounded"><Edit3 className="w-3 h-3 text-gray-500" /></button>
                      <button onClick={() => { if (confirm(`${ma.name} wirklich löschen?`)) onDelete(ma.id); }} className="p-1 hover:bg-red-100 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50">
              <td colSpan={4} className="py-2.5 px-3 font-semibold text-gray-900">Gesamt</td>
              <td className="py-2.5 px-3 text-right font-semibold">{formatEuro(mitarbeiter.reduce((s, m) => s + m.brutto_gehalt, 0))}</td>
              <td className="py-2.5 px-3 text-right font-semibold">{formatEuro(mitarbeiter.reduce((s, m) => s + m.ag_anteil_sv, 0))}</td>
              <td className="py-2.5 px-3 text-right font-semibold">{formatEuro(mitarbeiter.reduce((s, m) => s + (m.sonstige_kosten || 0), 0))}</td>
              <td className="py-2.5 px-3 text-right font-bold text-red-600">{formatEuro(gesamtKosten)}</td>
              <td></td>
            </tr>
            <tr className="bg-orange-50">
              <td colSpan={4} className="py-2 px-3 text-xs text-orange-700 font-medium">Jährlich</td>
              <td colSpan={4} className="py-2 px-3 text-right text-xs font-bold text-orange-700">{formatEuro(gesamtKosten * 12)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold">{editMa ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <MitarbeiterForm initial={editMa} onSave={(ma: any) => { onSave(ma); setShowForm(false); }} />
          </div>
        </div>
      )}
    </div>
  );
}

function MitarbeiterForm({ initial, onSave }: { initial: any; onSave: (ma: any) => void }) {
  const [f, setF] = useState({
    name: initial?.name || '', position: initial?.position || '',
    firma: initial?.firma || 'sv_buero_koop', typ: initial?.typ || 'vollzeit',
    brutto_gehalt: initial?.brutto_gehalt || 0, ag_anteil_sv: initial?.ag_anteil_sv || 0,
    sonstige_kosten: initial?.sonstige_kosten || 0, notiz: initial?.notiz || '',
  });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-xs font-medium text-gray-600 mb-1 block">Name</label><input className="w-full border rounded-lg px-3 py-2 text-sm" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
        <div><label className="text-xs font-medium text-gray-600 mb-1 block">Position</label><input className="w-full border rounded-lg px-3 py-2 text-sm" value={f.position} onChange={e => setF({ ...f, position: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-xs font-medium text-gray-600 mb-1 block">Firma</label>
          <select className="w-full border rounded-lg px-3 py-2 text-sm" value={f.firma} onChange={e => setF({ ...f, firma: e.target.value })}>
            <option value="sv_buero_koop">SV Büro Koop</option><option value="lcs">LCS GmbH</option>
          </select>
        </div>
        <div><label className="text-xs font-medium text-gray-600 mb-1 block">Typ</label>
          <select className="w-full border rounded-lg px-3 py-2 text-sm" value={f.typ} onChange={e => setF({ ...f, typ: e.target.value })}>
            <option value="vollzeit">Vollzeit</option><option value="teilzeit">Teilzeit</option><option value="minijob">Minijob</option><option value="freelancer">Freelancer</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div><label className="text-xs font-medium text-gray-600 mb-1 block">Brutto</label><input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={f.brutto_gehalt} onChange={e => setF({ ...f, brutto_gehalt: Number(e.target.value) })} /></div>
        <div><label className="text-xs font-medium text-gray-600 mb-1 block">AG-SV</label><input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={f.ag_anteil_sv} onChange={e => setF({ ...f, ag_anteil_sv: Number(e.target.value) })} /></div>
        <div><label className="text-xs font-medium text-gray-600 mb-1 block">Sonstige</label><input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={f.sonstige_kosten} onChange={e => setF({ ...f, sonstige_kosten: Number(e.target.value) })} /></div>
      </div>
      <div><label className="text-xs font-medium text-gray-600 mb-1 block">Notiz</label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={f.notiz} onChange={e => setF({ ...f, notiz: e.target.value })} /></div>
      <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
        <span className="text-sm text-gray-600">Gesamtkosten / Monat</span>
        <span className="text-lg font-bold text-red-600">{formatEuro(f.brutto_gehalt + f.ag_anteil_sv + f.sonstige_kosten)}</span>
      </div>
      <button onClick={() => onSave({ ...(initial ? { id: initial.id } : {}), ...f, aktiv: true })}
        className="w-full py-2.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center gap-2">
        <Save className="w-4 h-4" /> Speichern
      </button>
    </div>
  );
}

// ============================================================
// HAUPTKOMPONENTE
// ============================================================
export default function LiquiditaetPage() {
  const [selectedFirma, setSelectedFirma] = useState('alle');
  const [ansicht, setAnsicht] = useState<'tabelle' | 'chart' | 'personal'>('tabelle');
  const [showKpis, setShowKpis] = useState(true);
  const [showQualitaet, setShowQualitaet] = useState(false);
  const [, setLocation] = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    monatsDaten, tabellenZeilen, mitarbeiter, stats, loading,
    expandedRows, toggleRow, saveMitarbeiter, deleteMitarbeiter, reload, formatMonatLabel
  } = useHistorischeDaten(selectedFirma);

  const monate = useMemo(() => monatsDaten.map(m => m.monat), [monatsDaten]);
  const prognoseMonate = useMemo(() => new Set(monatsDaten.filter(m => m.istPrognose).map(m => m.monat)), [monatsDaten]);
  const gaps = useMemo(() => detectGaps(monate), [monate]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Liquiditätsdaten werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Liquiditätsplanung</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {stats.monate} Monate IST-Daten
              {stats.zeitraumVon ? ` (${formatMonatLabel(stats.zeitraumVon)} – ${formatMonatLabel(stats.zeitraumBis)})` : ''}
              {' '}+ 6 Monate Prognose
              {gaps.size > 0 && <span className="text-amber-500 ml-2">• {gaps.size} Datenlücke{gaps.size > 1 ? 'n' : ''}</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              {FIRMEN.map(f => (
                <button key={f.id} onClick={() => setSelectedFirma(f.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedFirma === f.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button onClick={() => setAnsicht('tabelle')} className={`p-1.5 rounded-md transition-all ${ansicht === 'tabelle' ? 'bg-white shadow' : ''}`} title="Tabelle"><Table className="w-4 h-4 text-gray-600" /></button>
              <button onClick={() => setAnsicht('chart')} className={`p-1.5 rounded-md transition-all ${ansicht === 'chart' ? 'bg-white shadow' : ''}`} title="Chart"><BarChart3 className="w-4 h-4 text-gray-600" /></button>
              <button onClick={() => setAnsicht('personal')} className={`p-1.5 rounded-md transition-all ${ansicht === 'personal' ? 'bg-white shadow' : ''}`} title="Personal"><Users className="w-4 h-4 text-gray-600" /></button>
            </div>
            <button onClick={() => setShowQualitaet(!showQualitaet)} className={`p-2 rounded-lg transition-colors ${showQualitaet ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-500'}`} title="Datenqualität">
              <ShieldCheck className="w-4 h-4" />
            </button>
            <button onClick={() => setShowKpis(!showKpis)} className="p-2 hover:bg-gray-100 rounded-lg" title={showKpis ? 'KPIs ausblenden' : 'KPIs einblenden'}>
              {showKpis ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
            </button>
            <button onClick={reload} className="p-2 hover:bg-gray-100 rounded-lg" title="Aktualisieren"><RefreshCw className="w-4 h-4 text-gray-500" /></button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-5">
        {/* Klärungs-Banner */}
        {stats.offeneKlaerungen > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {stats.offeneKlaerungen.toLocaleString('de-DE')} Transaktionen benötigen Klärung
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Die Liquiditätsplanung zeigt nur sicher klassifizierte Transaktionen. Klären Sie offene Posten für ein vollständiges Bild.
                </p>
              </div>
            </div>
            <button
              onClick={() => setLocation('/transaktionen/klaerung')}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
            >
              Jetzt klären <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Datenqualitäts-Banner */}
        {showQualitaet && (
          <DatenqualitaetBanner monatsDaten={monatsDaten} offeneKlaerungen={stats.offeneKlaerungen} prognoseMonate={prognoseMonate} />
        )}

        {showKpis && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <KpiKarte label="Ø Einzahlungen" wert={formatEuro(stats.avgEinnahmen)} sub="pro Monat" icon={ArrowUpRight} farbe="#10b981" trend="up" />
            <KpiKarte label="Ø Auszahlungen" wert={formatEuro(stats.avgAusgaben)} sub="pro Monat" icon={ArrowDownRight} farbe="#ef4444" trend="neutral" />
            <KpiKarte label="Ø Cashflow" wert={formatEuro(stats.avgCashflow)} sub={stats.avgCashflow >= 0 ? 'positiv' : 'negativ'} icon={Activity} farbe="#8b5cf6" trend={stats.avgCashflow >= 0 ? 'up' : 'down'} />
            <KpiKarte label="Gesamt Cashflow" wert={formatEuro(stats.gesamtCashflow)} sub={`${stats.monate} Monate`} icon={Wallet} farbe="#3b82f6" trend={stats.gesamtCashflow >= 0 ? 'up' : 'down'} />
            <KpiKarte label="Personal / Monat" wert={formatEuro(mitarbeiter.reduce((s, m) => s + (m.gesamt_kosten || m.brutto_gehalt + m.ag_anteil_sv), 0))} sub={`${mitarbeiter.length} Mitarbeiter`} icon={Users} farbe="#f97316" trend="neutral" />
            <KpiKarte label="Firmen" wert={stats.firmen.length.toString()} sub={selectedFirma === 'alle' ? 'konsolidiert' : FIRMEN.find(f => f.id === selectedFirma)?.label || ''} icon={Building2} farbe="#6366f1" trend="neutral" />
          </div>
        )}

        {/* SYNCHRONISIERTER CHART + TABELLE */}
        {(ansicht === 'chart' || ansicht === 'tabelle') && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900">Cashflow-Verlauf & Tabelle</h3>
                {gaps.size > 0 && (
                  <span className="text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {gaps.size} Datenlücke{gaps.size > 1 ? 'n' : ''} erkannt
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-200" /> Prognose</div>
                {gaps.size > 0 && <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-300" /> Lücke</div>}
                <div className="flex items-center gap-1"><Info className="w-3 h-3" /> Klicken zum Aufklappen</div>
              </div>
            </div>

            {/* Scrollbarer Container für Chart + Tabelle */}
            <div ref={scrollContainerRef} className="overflow-x-auto">
              {/* Chart */}
              <div className="border-b border-gray-100 px-2 py-3">
                <IntegrierterChart monatsDaten={monatsDaten} gaps={gaps} formatLabel={formatMonatLabel} />
              </div>

              {/* Tabelle — gleiche Spaltenbreiten wie Chart */}
              {ansicht === 'tabelle' && (
                <table className="w-full text-sm border-collapse" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: `${LABEL_COL_WIDTH}px` }} />
                    {monate.map((m, idx) => (
                      <React.Fragment key={m}>
                        {gaps.has(idx) && <col style={{ width: `${GAP_COL_WIDTH}px` }} />}
                        <col style={{ width: `${COL_WIDTH}px` }} />
                      </React.Fragment>
                    ))}
                    <col style={{ width: `${SUMMARY_COL_WIDTH}px` }} />
                    <col style={{ width: `${SUMMARY_COL_WIDTH}px` }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="sticky left-0 z-20 bg-gray-50 text-left px-3 py-2.5 text-xs font-semibold text-gray-600"
                        style={{ width: `${LABEL_COL_WIDTH}px` }}>
                        Kategorie
                      </th>
                      {monate.map((m, idx) => {
                        const istP = prognoseMonate.has(m);
                        return (
                          <React.Fragment key={m}>
                            {gaps.has(idx) && (
                              <th className="bg-amber-50/80 border-x border-dashed border-amber-200 text-center"
                                style={{ width: `${GAP_COL_WIDTH}px` }}>
                                <div className="text-[7px] text-amber-500 font-medium leading-tight">
                                  {idx > 0 ? formatGap(monate[idx - 1], m, formatMonatLabel) : ''}
                                </div>
                              </th>
                            )}
                            <th className={`text-right px-2 py-2.5 text-[10px] font-semibold text-gray-500 ${istP ? 'bg-blue-50/50' : ''}`}
                              style={{ width: `${COL_WIDTH}px` }}>
                              {formatMonatLabel(m)}
                              {istP && <span className="block text-[8px] text-blue-400 font-normal">Prognose</span>}
                            </th>
                          </React.Fragment>
                        );
                      })}
                      <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-600 bg-gray-100 border-l border-gray-200"
                        style={{ width: `${SUMMARY_COL_WIDTH}px` }}>
                        Summe
                      </th>
                      <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-600 bg-gray-100"
                        style={{ width: `${SUMMARY_COL_WIDTH}px` }}>
                        Ø/Monat
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabellenZeilen.map(zeile => (
                      <ZeileRow key={zeile.id} zeile={zeile} monate={monate} expandedRows={expandedRows}
                        onToggle={toggleRow} formatLabel={formatMonatLabel} prognoseMonate={prognoseMonate} gaps={gaps} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {ansicht === 'personal' && (
          <MitarbeiterSection mitarbeiter={mitarbeiter} onSave={saveMitarbeiter} onDelete={deleteMitarbeiter} />
        )}
      </div>
    </div>
  );
}

// React import for Fragment
import React from 'react';
